import React, { useState } from "react";
import cn from "classnames";
import { Icon } from "assets/icons";

import {
  BoldText,
  MediumText,
  SemiBoldText,
  RegularText,
} from "components/Text";
import { Button } from "components/Button";
import { Radio } from "components/Radio";
import { Spinner } from "components/Feedback";

import styles from "./styles.module.scss";

/**
 * Шаблоны модалок. Все модалки строятся из этих блоков:
 * <ModalShell> — контейнер (белый фон, паддинги)
 *   <ModalHeader title onClose /> — заголовок + крестик
 *   <ModalBody> — контент
 *   <ModalFooter> — кнопки действий
 */

export const ModalShell = ({ children, width, height, className }) => (
  <div className={cn(styles.shell, className)} style={{ width, height }}>
    {children}
  </div>
);

export const ModalHeader = ({ title, onClose }) => (
  <div className={styles.header}>
    <BoldText>{title}</BoldText>
    {!!onClose && (
      <button type="button" className={styles.closeButton} onClick={onClose}>
        <Icon type="close" size={15} />
      </button>
    )}
  </div>
);

export const ModalBody = ({ children, className }) => (
  <div className={cn(styles.body, className)}>{children}</div>
);

/** align: "end" (по умолчанию, кнопки справа) | "start" (модалки документов) */
export const ModalFooter = ({ children, className, align = "end" }) => (
  <div
    className={cn(
      styles.footer,
      align === "start" && styles.footerStart,
      className
    )}
  >
    {children}
  </div>
);

/** Модалка подтверждения (удаление и т.п.) */
export const ConfirmationModal = ({
  title,
  onConfirm,
  onClose,
  confirmText = "Удалить",
  cancelText = "Нет",
  danger = true,
  width = 400,
}) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (!onConfirm) return onClose?.();
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell width={width} className={styles.dialog}>
      <button type="button" className={styles.dialogClose} onClick={onClose}>
        <Icon type="close" size={15} />
      </button>
      <SemiBoldText className={styles.dialogTitle}>{title}</SemiBoldText>
      <div className={styles.dialogButtons}>
        <Button
          variant={danger ? "danger" : "secondary"}
          onClick={handleConfirm}
          loading={loading}
        >
          {confirmText}
        </Button>
        <Button variant="tertiary" onClick={onClose}>
          {cancelText}
        </Button>
      </div>
    </ModalShell>
  );
};

/** Информационная модалка с одной кнопкой */
export const InfoModal = ({
  title,
  onOkClick,
  onClose,
  buttonText = "Ок",
  width = 400,
}) => (
  <ModalShell width={width} className={styles.dialog}>
    <button type="button" className={styles.dialogClose} onClick={onClose}>
      <Icon type="close" size={15} />
    </button>
    <SemiBoldText className={styles.dialogTitle}>{title}</SemiBoldText>
    <div className={styles.dialogButtons}>
      <Button variant="secondary" onClick={onOkClick || onClose}>
        {buttonText}
      </Button>
    </div>
  </ModalShell>
);

/** Модалка загрузки */
export const LoadingModal = ({ title = "Загрузка..." }) => (
  <ModalShell width={310} className={styles.dialog}>
    <Spinner size={32} />
    <MediumText size={16}>{title}</MediumText>
  </ModalShell>
);

/** Модалка выбора одного варианта (радио-список) */
export const SelectModal = ({
  title,
  options = [],
  onSelect,
  onClose,
  buttonText = "Выбрать",
}) => {
  const [selected, setSelected] = useState(null);

  return (
    <ModalShell width={420} className={styles.dialog}>
      <button type="button" className={styles.dialogClose} onClick={onClose}>
        <Icon type="close" size={15} />
      </button>
      <MediumText size={16} className={styles.dialogTitle}>
        {title}
      </MediumText>
      <div className={styles.radioList}>
        {options.length ? (
          options.map((option) => (
            <Radio
              key={option.value}
              id={`select-modal-${option.value}`}
              name="select-modal"
              label={option.label}
              checked={selected?.value === option.value}
              onChange={() => setSelected(option)}
            />
          ))
        ) : (
          <RegularText color="muted">Пусто.</RegularText>
        )}
      </div>
      <div className={styles.dialogButtons}>
        <Button
          variant="secondary"
          disabled={!selected}
          onClick={() => onSelect?.(selected)}
        >
          {buttonText}
        </Button>
        <Button variant="tertiary" onClick={onClose}>
          Отмена
        </Button>
      </div>
    </ModalShell>
  );
};

/** Модалка ошибки */
export const ErrorModal = ({ title, message, onClose }) => (
  <ModalShell width={420} className={styles.dialog}>
    <button type="button" className={styles.dialogClose} onClick={onClose}>
      <Icon type="close" size={15} />
    </button>
    <SemiBoldText className={styles.dialogTitle}>
      {title || "Ошибка"}
    </SemiBoldText>
    {!!message && (
      <RegularText size={13} color="danger" className={styles.dialogMessage}>
        {message}
      </RegularText>
    )}
    <div className={styles.dialogButtons}>
      <Button variant="secondary" onClick={onClose}>
        Ок
      </Button>
    </div>
  </ModalShell>
);
